import Bugsnag from '@bugsnag/js'
import BugsnagPluginVue, {BugsnagPluginVueResult} from '@bugsnag/plugin-vue'
import type {UserModule} from '@/types'
import {getErrorEventDiagnostics, getUnhandledRejectionBreadcrumb, getWindowErrorBreadcrumb} from '@/composables/bugsnagDiagnostics'

export const install: UserModule = ({ app, router }) => {
    if (import.meta.env.SSR) {
      return
    }

    const appVersion = import.meta.env.VITE_APP_VERSION || 'unknown'
    const routePattern = () => router.currentRoute.value.matched.at(-1)?.path
    // Capture this before Bugsnag's bubbling handler so the next InvalidError
    // includes a non-sensitive classification of the original rejection.
    window.addEventListener('unhandledrejection', (event) => {
      const breadcrumb = getUnhandledRejectionBreadcrumb(
        event.reason,
        routePattern() || 'unmatched',
        appVersion,
      )

      if (breadcrumb) {
        Bugsnag.leaveBreadcrumb('Unhandled non-Error promise rejection', breadcrumb, 'error')
      }
    }, true)

    // Preserve the original error while recording only its script-origin class.
    // This makes stackless errors distinguishable without collecting URLs.
    window.addEventListener('error', (event) => {
      Bugsnag.leaveBreadcrumb('Window error source', getWindowErrorBreadcrumb(
        event.filename,
        window.location.origin,
        routePattern() || 'unmatched',
        appVersion,
        event.error,
        event.lineno,
        event.colno,
      ), 'error')
    }, true)

    Bugsnag.start({
      apiKey: import.meta.env.VITE_BUGSNAG_API_KEY as string,
      autoTrackSessions: false,
      enabledReleaseStages: ['production'],
      plugins: [new BugsnagPluginVue()],
      appVersion,
      // Attach context to every notifier event, including Vue errors that do
      // not emit a window error. Do not replace errors, stacks or release data,
      // and do not return false (which would suppress the original report).
      onError(event) {
        event.addMetadata('diagnostics', getErrorEventDiagnostics(
          routePattern(),
          appVersion,
          import.meta.url,
          event.errors[0]?.stacktrace.map(frame => frame.file) || [],
          window.location.origin,
        ))
      },
    });

    const bugsnagVue = Bugsnag.getPlugin('vue') as BugsnagPluginVueResult
    app.use(bugsnagVue)
};
